import { useState } from "react";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import { toast, Toaster } from "sonner";
import { brand, socialLinks, pageMeta } from "@/data/mockData";
import { db } from "@/lib/firebase";

const INTEREST = ["Buy", "Sell", "Rent", "Upgrade", "Invest", "General Advice"];
const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    interest: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const name = form.name.trim();
    const email = form.email.trim();
    const message = form.message.trim();
    const interest = form.interest;

    if (!name || !email || !interest || !message) {
      toast.error("Please fill in name, email, interest, and message.");
      return;
    }
    if (!EMAIL_RX.test(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }

    setSubmitting(true);
    try {
      await addDoc(collection(db, "contactSubmissions"), {
        name,
        email,
        phone: form.phone.trim(),
        interest,
        message,
        read: false,
        source: "website-contact-form",
        createdAt: serverTimestamp(),
      });
      toast.success("Thanks! Johnny will reach out shortly. For instant response, WhatsApp him.");
      setForm({ name: "", email: "", phone: "", interest: "", message: "" });
    } catch (err) {
      console.error("Contact submission failed", err);
      toast.error("Submission failed. Please try WhatsApp or call directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEO {...pageMeta.contact} />
      <Toaster position="top-right" richColors />

      <section className="bg-white pt-20 pb-12 md:py-24" data-testid="contact-hero">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#0a2540]/60 font-semibold mb-4">
              Contact
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#0a2540] leading-[1.05]">
              Schedule an Appointment.
            </h1>
            <p className="mt-7 text-base text-slate-600 leading-relaxed max-w-2xl mx-auto">
              For fastest response, WhatsApp Johnny directly. Otherwise, send the form below — typical response within one working day.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Quick channels */}
      <section className="bg-[#fafafa] py-12 border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-3 gap-4">
          <a
            href={brand.whatsapp}
            target="_blank"
            rel="noreferrer"
            data-testid="contact-whatsapp-card"
            className="bg-white border border-gray-100 hover:border-[#25D366] rounded-2xl p-6 flex items-center gap-5 hover:-translate-y-0.5 transition-all group"
          >
            <div className="h-12 w-12 rounded-full bg-[#25D366]/10 inline-flex items-center justify-center group-hover:bg-[#25D366] transition-colors">
              <i className="fa-brands fa-whatsapp text-2xl text-[#25D366] group-hover:text-white" aria-hidden="true" />
            </div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Fastest</div>
              <div className="font-serif text-[#0a2540] text-lg leading-tight">WhatsApp Johnny</div>
              <div className="text-sm text-slate-500">{brand.phone}</div>
            </div>
            <i className="fa-solid fa-arrow-right text-slate-400 group-hover:text-[#0a2540] group-hover:translate-x-1 transition-all" aria-hidden="true" />
          </a>
          <a
            href={`tel:${brand.phoneRaw}`}
            data-testid="contact-phone-card"
            className="bg-white border border-gray-100 hover:border-[#ffde59] rounded-2xl p-6 flex items-center gap-5 hover:-translate-y-0.5 transition-all group"
          >
            <div className="h-12 w-12 rounded-full bg-[#fffbea] inline-flex items-center justify-center group-hover:bg-[#ffde59] transition-colors">
              <i className="fa-solid fa-phone text-xl text-[#023e8a]" aria-hidden="true" />
            </div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Direct Call</div>
              <div className="font-serif text-[#0a2540] text-lg leading-tight">{brand.phone}</div>
              <div className="text-sm text-slate-500">Mon–Sun, 9am–9pm</div>
            </div>
            <i className="fa-solid fa-arrow-right text-slate-400 group-hover:text-[#0a2540] group-hover:translate-x-1 transition-all" aria-hidden="true" />
          </a>
          <a
            href={brand.linktree}
            target="_blank"
            rel="noreferrer"
            data-testid="contact-linktree-card"
            className="bg-white border border-gray-100 hover:border-[#0a2540] rounded-2xl p-6 flex items-center gap-5 hover:-translate-y-0.5 transition-all group"
          >
            <div className="h-12 w-12 rounded-full bg-slate-100 inline-flex items-center justify-center group-hover:bg-[#0a2540] transition-colors">
              <i className="fa-solid fa-link text-xl text-[#0a2540] group-hover:text-white" aria-hidden="true" />
            </div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">All Links</div>
              <div className="font-serif text-[#0a2540] text-lg leading-tight">Linktree</div>
              <div className="text-sm text-slate-500">linktr.ee/meproperty</div>
            </div>
            <i className="fa-solid fa-arrow-right text-slate-400 group-hover:text-[#0a2540] group-hover:translate-x-1 transition-all" aria-hidden="true" />
          </a>
        </div>
      </section>

      {/* Form + Socials */}
      <section className="bg-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-[1.6fr,1fr] gap-12">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl text-[#0a2540] mb-3 leading-tight">
              Send a message.
            </h2>
            <p className="text-slate-600 mb-8">
              Tell Johnny what's on your mind — purchase plan, valuation question, rental advice, or any other property matters.
            </p>

            <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form" noValidate>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs uppercase tracking-wider font-semibold text-[#0a2540] mb-2">Name *</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={update("name")}
                    data-testid="contact-name"
                    className="w-full px-4 py-3.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#0a2540] focus:ring-2 focus:ring-[#ffde59]/40 bg-white"
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider font-semibold text-[#0a2540] mb-2">Email *</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    data-testid="contact-email"
                    className="w-full px-4 py-3.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#0a2540] focus:ring-2 focus:ring-[#ffde59]/40 bg-white"
                    placeholder="you@example.com"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider font-semibold text-[#0a2540] mb-2">Phone</label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={update("phone")}
                  data-testid="contact-phone"
                  className="w-full px-4 py-3.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#0a2540] focus:ring-2 focus:ring-[#ffde59]/40 bg-white"
                  placeholder="+65 XXXX XXXX"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider font-semibold text-[#0a2540] mb-3">Seek Advice On *</label>
                <div className="flex flex-wrap gap-2">
                  {INTEREST.map((opt) => (
                    <button
                      type="button"
                      key={opt}
                      onClick={() => setForm((f) => ({ ...f, interest: opt }))}
                      data-testid={`contact-interest-${opt.toLowerCase().replace(/\s+/g, "-")}`}
                      className={`px-4 py-2.5 text-xs font-medium uppercase tracking-wider rounded-full transition-all ${
                        form.interest === opt
                          ? "bg-[#0a2540] text-white"
                          : "bg-white text-slate-600 border border-gray-200 hover:border-[#0a2540]"
                      }`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider font-semibold text-[#0a2540] mb-2">Message *</label>
                <textarea
                  rows={5}
                  value={form.message}
                  onChange={update("message")}
                  data-testid="contact-message"
                  className="w-full px-4 py-3.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#0a2540] focus:ring-2 focus:ring-[#ffde59]/40 bg-white resize-none"
                  placeholder="Briefly describe your situation, goals, or what you'd like to know..."
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit"
                className="inline-flex items-center gap-2 bg-[#ffde59] text-[#023e8a] px-7 py-4 font-semibold text-base rounded-full hover:shadow-xl hover:-translate-y-0.5 transition-all disabled:opacity-50"
              >
                {submitting ? "Sending..." : "Schedule an Appointment"}
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
              </button>
              <p className="text-xs text-slate-500 mt-2">
                {/* BACKEND INTEGRATION: will POST to /api/contact when backend is ready. */}
                By submitting, you agree to be contacted by Johnny regarding your enquiry.
              </p>
            </form>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="bg-[#fafafa] rounded-3xl p-8 lg:sticky lg:top-28">
              <h3 className="font-serif text-xl text-[#0a2540] mb-6">Find Johnny online.</h3>
              <div className="grid grid-cols-1 gap-2">
                {socialLinks.map((s) => (
                  <a
                    key={s.url}
                    href={s.url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`contact-social-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="flex items-center gap-4 px-4 py-3.5 bg-white rounded-xl border border-gray-100 hover:border-[#ffde59] hover:bg-[#fffbea] transition-all group"
                  >
                    <i className={`${s.icon} text-lg text-[#0a2540]`} aria-hidden="true" />
                    <span className="text-sm text-slate-700 flex-1 font-medium">{s.label}</span>
                    <i className="fa-solid fa-arrow-up-right-from-square text-[10px] text-slate-400" aria-hidden="true" />
                  </a>
                ))}
              </div>
              <div className="mt-6 pt-6 border-t border-gray-200 text-[11px] text-slate-400 font-mono space-y-0.5">
                <div>CEA: {brand.cea}</div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
